import { userError } from "@/lib/userError";
import React, { useState } from "react";
import { TrendingUp, ShieldCheck, CheckCircle2, AlertOctagon, FileText, Target, Info } from "lucide-react";
import { getGrowth, acceptExtractedTask, draftWeeklyUpdate } from "@/lib/apiClient";
import { useData } from "@/lib/useData";
import { PageHeader, LoadingCards, ErrorState, EmptyState } from "@/components/Common";
import { StatusBadge } from "@/components/StatusBadge";
import { PRIORITY, fiRelative } from "@/lib/constants";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import GrowthProposals from "@/components/GrowthProposals";

const severityTone = { high: "text-rose-400 bg-rose-500/10", medium: "text-amber-400 bg-amber-500/10", low: "text-sky-400 bg-sky-500/10" };

export default function Growth() {
  const { data, loading, error, refetch } = useData(getGrowth);
  const [weekly, setWeekly] = useState(null);
  const [busy, setBusy] = useState(false);

  const accept = async (aid, xid) => {
    try {
      await acceptExtractedTask(aid, xid);
      toast.success("Tehtävä hyväksytty ja lisätty tehtäviin.");
      await refetch();
    } catch (requestError) {
      toast.error(userError(requestError, "Tehtävän hyväksyminen epäonnistui."));
    }
  };

  const genWeekly = async () => {
    setBusy(true);
    try {
      const res = await draftWeeklyUpdate();
      setWeekly(res.draft);
      toast.success("Viikkoraportin luonnos luotu.");
    } catch (requestError) {
      toast.error(userError(requestError, "Viikkoraportin luonnoksen luominen epäonnistui."));
    } finally { setBusy(false); }
  };

  if (loading) return <><PageHeader icon={TrendingUp} title="Kasvujohtaja" /><LoadingCards count={4} /></>;
  if (error) return <><GrowthProposals /><ErrorState onRetry={refetch} /></>;

  const src = data.trusted_source;
  if (!src) {
    return (
      <div>
        <PageHeader icon={TrendingUp} title="Kasvujohtaja" subtitle="Viikoittaiset tavoitteet ja tehtävät." />
        <GrowthProposals />
        <EmptyState icon={TrendingUp} title="Kasvujohtajan projektitietoja ei ole vielä yhdistetty" hint="Projektitiedot ja henkilökohtaiset tehtäväehdotukset ovat erillisiä yhteyksiä." />
      </div>
    );
  }
  return (
    <div className="space-y-6">
      <PageHeader icon={TrendingUp} title="Kasvujohtaja" subtitle="Luotetun lähettäjän työtila." />
      <GrowthProposals />

      {/* Trusted profile */}
      <div className="rounded-2xl border border-primary/25 bg-primary/5 p-4 fade-up" data-testid="growth-profile">
        <div className="flex items-center gap-3">
          <Avatar className="h-12 w-12 border-2 border-primary/40">
            <AvatarImage src={src.avatar} alt={src.name} />
            <AvatarFallback>MS</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="font-heading font-bold text-foreground">{src.name}</p>
              <ShieldCheck className="h-4 w-4 text-primary" />
            </div>
            <p className="text-xs text-muted-foreground">{src.role} · {src.email}</p>
          </div>
        </div>
      </div>

      {/* Data-not-instructions notice */}
      <div className="flex gap-2 rounded-2xl border border-amber-500/25 bg-amber-500/5 p-3 text-xs text-amber-500" data-testid="growth-data-notice">
        <Info className="h-4 w-4 shrink-0 mt-0.5" />
        <p>{data.data_notice}</p>
      </div>

      {/* Objectives */}
      <section className="space-y-3">
        <h2 className="font-heading font-bold text-foreground flex items-center gap-2 text-sm"><Target className="h-4 w-4 text-primary" /> Edistyminen</h2>
        {data.objectives.map((o) => (
          <div key={o.id} className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-foreground">{o.title}</p>
              <span className="font-mono text-sm font-semibold text-primary">{o.progress}%</span>
            </div>
            <Progress value={o.progress} className="h-2" />
          </div>
        ))}
      </section>

      {/* Weekly assignments */}
      <section className="space-y-3">
        <h2 className="font-heading font-bold text-foreground flex items-center gap-2 text-sm"><FileText className="h-4 w-4 text-primary" /> Viikon toimeksiannot</h2>
        {data.assignments.map((a) => (
          <div key={a.id} className="rounded-2xl border border-border bg-card p-4 fade-up" data-testid={`assignment-${a.id}`}>
            <p className="font-medium text-foreground">{a.title}</p>
            <p className="text-[11px] text-muted-foreground mb-2">{fiRelative(a.received_at)}</p>
            <p className="text-sm text-muted-foreground border-l-2 border-border pl-3">{a.body}</p>

            <p className="text-xs font-semibold text-foreground mt-4 mb-2">Poimitut tehtävät (luonnokset):</p>
            <div className="space-y-2">
              {a.extracted_tasks.map((xt) => (
                <div key={xt.id} className="flex items-center justify-between gap-2 rounded-xl border border-border bg-background p-3" data-testid={`extracted-${xt.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm text-foreground truncate">{xt.title}</p>
                    <span className={`mt-1 inline-block rounded-full border px-2 py-0.5 text-[10px] font-semibold ${PRIORITY[xt.priority].className}`}>{PRIORITY[xt.priority].label}</span>
                  </div>
                  {xt.status === "Hyväksytty" ? (
                    <StatusBadge status="Hyväksytty" />
                  ) : (
                    <button type="button" data-testid={`accept-${xt.id}`} onClick={() => accept(a.id, xt.id)}
                      className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground active:scale-95 transition-transform shrink-0">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Hyväksy tehtävä
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* Blockers */}
      <section className="space-y-3">
        <h2 className="font-heading font-bold text-foreground flex items-center gap-2 text-sm"><AlertOctagon className="h-4 w-4 text-rose-400" /> Tunnistetut esteet</h2>
        <div className="space-y-2" data-testid="blockers">
          {data.blockers.map((b) => (
            <div key={b.id} className={`rounded-2xl p-3 text-sm flex items-center gap-2 ${severityTone[b.severity]}`}>
              <AlertOctagon className="h-4 w-4 shrink-0" /> {b.title}
            </div>
          ))}
        </div>
      </section>

      {/* Weekly update draft */}
      <section className="space-y-3">
        <button type="button" data-testid="gen-weekly-btn" onClick={genWeekly} disabled={busy}
          className="w-full rounded-2xl bg-primary py-3 font-heading font-bold text-primary-foreground active:scale-95 transition-transform disabled:opacity-50">
          {busy ? "Luodaan…" : "Generoi viikkoraportti Kasvujohtajalle"}
        </button>
        {weekly && (
          <div className="rounded-2xl border border-border bg-card p-4 fade-up" data-testid="weekly-draft">
            <div className="flex items-center justify-between mb-2"><p className="font-heading font-bold text-foreground">Viikkoraportti</p><StatusBadge status="Luonnos" /></div>
            <pre className="whitespace-pre-wrap font-mono text-xs text-muted-foreground">{weekly}</pre>
            <p className="text-[11px] text-muted-foreground mt-3">Luonnos ei lähde ennen erillistä hyväksyntää.</p>
          </div>
        )}
      </section>
    </div>
  );
}
